import { isEqual, isUndefined, omitBy } from "lodash";
import uuid from "uuid/v4";
import dayjs from "dayjs";
import {
  consolidateAllProductLines,
  filterProduct,
  getPaymentAndDiscountPriceForGroup,
  isPreOrderProgramActive,
  splitQuantityOverStockAndPreOrder,
} from "./product";

/**
 * Pure cart mutations. Every function here takes the cart *as it is stored*
 * and returns the cart as it should be stored, without touching Firestore.
 * useCart runs them inside a Firestore transaction, which may execute the
 * same function more than once on contention - so nothing in this file may
 * have side effects beyond generating fresh ids and timestamps.
 */

/**
 * Firestore rejects `undefined` field values, and lodash's isEqual treats
 * `{ discount: undefined }` as different from `{}`. A CMS product without,
 * say, a discount field would therefore make every recalculation look
 * "changed" and make every write fail. Strip such keys before comparing or
 * writing a line.
 */
export const stripUndefined = (line) => omitBy(line, isUndefined);

export const totalQuantity = (lines) =>
  (Array.isArray(lines) ? lines : []).reduce(
    (sum, line) => sum + (Number(line && line.quantity) || 0),
    0,
  );

/**
 * Add `quantity` of `lineProduct` to the cart: onto the matching line when
 * one exists (same product/variant and the same pre-order/unavailable flags,
 * see filterProduct), otherwise as a new line.
 */
export const addQuantityToCartLine = (cart, lineProduct, quantity) => {
  const qty = Number(quantity) || 0;
  if (qty <= 0) return cart;
  const existing = filterProduct(cart, lineProduct);
  if (existing === undefined) {
    return [
      ...cart,
      stripUndefined({
        ...lineProduct,
        quantity: qty,
        cartItemId: uuid(),
        addedAt: dayjs().unix(),
      }),
    ];
  }
  return cart.map((item) =>
    item.cartItemId === existing.cartItemId
      ? { ...item, quantity: (item.quantity || 0) + qty }
      : item,
  );
};

/**
 * Add a product with the stock / pre-order split applied. Returns the new
 * cart plus the split that was actually used, so the caller can report it.
 */
export const computeAddToCart = (rawCart, { product, quantity }) => {
  const orderUnit = product.orderUnit || 1;
  const programActive = isPreOrderProgramActive(product.preOrder);

  // Heal any pre-existing duplicate normal/pre-order lines for this exact
  // product before adding more, so the new addition lands on a clean baseline
  const currentCart = consolidateAllProductLines(rawCart, (id) =>
    id === product.id
      ? { stock: product.stock, orderUnit, preOrder: product.preOrder }
      : null,
  );

  const requested = quantity || 1;

  // Caller explicitly targets a pre-order line: add there directly without
  // consuming stock - but only while the program is actually running
  if (product.preOrderLine && programActive) {
    return {
      products: addQuantityToCartLine(
        currentCart,
        { ...product, preOrderLine: true },
        requested,
      ),
      normalQty: 0,
      preOrderQty: requested,
    };
  }

  // Units of stock already taken by the normal lines of this product
  const normalInCart = currentCart
    .filter((it) => it.id === product.id && !it.preOrderLine)
    .reduce((s, it) => s + (it.quantity || 0), 0);
  const stock = product.stock || 0;
  const inStockLeft = Math.max(0, stock - normalInCart);

  // Allocate from stock in multiples of orderUnit
  let allocatable = Math.floor(inStockLeft / orderUnit) * orderUnit;
  // Defensive: if no normal line exists and stock exists but allocatable 0, base on stock directly
  if (allocatable === 0 && stock >= orderUnit && normalInCart === 0) {
    allocatable = Math.floor(stock / orderUnit) * orderUnit;
  }

  const normalQty = Math.min(requested, allocatable);
  // Surplus above stock is only orderable while the pre-order program runs;
  // once availableAt has passed, stock is all there is
  const preOrderQty = programActive ? Math.max(0, requested - normalQty) : 0;

  let newCart = currentCart;
  if (normalQty > 0) {
    newCart = addQuantityToCartLine(
      newCart,
      { ...product, preOrderLine: false },
      normalQty,
    );
  }
  if (preOrderQty > 0) {
    newCart = addQuantityToCartLine(
      newCart,
      { ...product, preOrderLine: true },
      preOrderQty,
    );
  }

  return { products: newCart, normalQty, preOrderQty };
};

/**
 * Bring a stored cart in line with the CMS as it is now: refresh product data
 * and prices, drop lines whose product vanished from the catalogue, and
 * rebuild the stock / pre-order / not-deliverable split per product.
 *
 * Quantities are never reduced here. The only way a unit leaves the cart is
 * with its product, and that is reported in `removed`. `quantityPreserved`
 * asserts exactly that, so a caller can refuse to write when it is false.
 */
export const computeUpdatedCart = (
  currentCart,
  { products, groupId, groupSettingsUserGroup, selectedSalesGroup },
) => {
  const removed = [];
  const unavailable = [];
  // Units of stock already taken by earlier lines of the same product, so
  // two lines never allocate the same units twice
  const stockClaimedByProduct = new Map();

  const refreshedCart = currentCart.reduce((updatedCart, cartItem) => {
    const productFound = products.find(
      (product) => product.id === cartItem.id,
    );

    if (!productFound) {
      removed.push(
        stripUndefined({
          id: cartItem.id,
          name: cartItem.name,
          quantity: cartItem.quantity,
        }),
      );
      return updatedCart;
    }

    const productPriceForUser = getPaymentAndDiscountPriceForGroup(
      productFound,
      groupId,
      groupSettingsUserGroup,
      selectedSalesGroup,
    );

    // Re-evaluate against the CMS as it is now, not as it was when the
    // line was added: stock first, the surplus only while the pre-order
    // program is still running. What is left over is not deliverable, but
    // stays in the cart as such - see the note in consolidateProductLines
    const stockClaimed = stockClaimedByProduct.get(cartItem.id) || 0;
    const { normalQty, preOrderQty, unavailableQty } =
      splitQuantityOverStockAndPreOrder(cartItem.quantity, {
        stock: productFound.stock,
        orderUnit: productFound.orderUnit,
        preOrder: productFound.preOrder,
        stockAlreadyClaimed: stockClaimed,
      });
    stockClaimedByProduct.set(cartItem.id, stockClaimed + normalQty);

    if (unavailableQty > 0) {
      unavailable.push(
        stripUndefined({
          id: cartItem.id,
          name: productFound.name,
          quantity: unavailableQty,
        }),
      );
    }

    updatedCart.push(
      stripUndefined({
        ...cartItem,
        name: productFound.name,
        image: productFound.image,
        price: productPriceForUser.original,
        discount: productFound.discount,
        taxRate: productFound.taxRate,
        stock: productFound.stock,
        // The CMS is the source of truth for the pre-order window; a snapshot
        // taken at add-to-cart time goes stale the moment the date is moved
        preOrder: productFound.preOrder || null,
        // Quantity stays whole; consolidateAllProductLines below cuts the
        // line into its stock / pre-order / not-deliverable parts
        preOrderLine: normalQty === 0 && preOrderQty > 0,
        unavailableLine: normalQty === 0 && preOrderQty === 0,
      }),
    );
    return updatedCart;
  }, []);

  // Heal duplicate lines for the same product/variant and rebuild the split
  // over stock / pre-order / not-deliverable from the current CMS state
  const consolidatedCart = consolidateAllProductLines(refreshedCart, (id) => {
    const productFound = products.find((product) => product.id === id);
    return productFound
      ? {
          stock: productFound.stock,
          orderUnit: productFound.orderUnit,
          preOrder: productFound.preOrder,
        }
      : null;
  }).map(stripUndefined);

  const totalBefore = totalQuantity(currentCart);
  const totalAfter = totalQuantity(consolidatedCart);
  const totalRemoved = totalQuantity(removed);

  return {
    products: consolidatedCart,
    changed: !isEqual(consolidatedCart, currentCart),
    removed,
    unavailable,
    totalBefore,
    totalAfter,
    quantityPreserved: totalAfter + totalRemoved === totalBefore,
  };
};
